import { Injectable } from '@angular/core';
import { ModalTracks } from '@src/app/core/config';
import {
  BlobMetum,
  Event,
  Geoboundary,
  Geotag,
  GeoTagMessageData,
  Group,
  Post
} from '@src/app/core/models/model';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ModalPostComponent } from './modal-post/modal-post.component';

export type PostComposerView = 'post' | 'geotag' | 'boundary';
export type PostComposerSharedEntity = Post | Event | Group;

export interface PostComposerOptions {
  initialView?: PostComposerView;
  files?: BlobMetum[];
  sharedEntity?: PostComposerSharedEntity;
  sharedEntityType?: string;
  sharedEventStyling?: boolean;
  geotag?: Geotag;
  boundary?: Geoboundary;
  coordinates?: string;
  identityIsHidden?: boolean;
  isUploadingFile?: boolean;
  showAuthor?: boolean;
}

export interface PostComposerResult {
  status: 'created' | 'cancelled';
  post?: Post;
  postId?: string;
  geotag?: Geotag;
  boundary?: Geoboundary;
}

/**
 * The single reusable entry point for creating a post.
 *
 * Consumers describe the content they want to compose and receive a normalized
 * result. Modal routing and the legacy modal response shape remain private here.
 */
@Injectable({ providedIn: 'root' })
export class PostComposerService {
  constructor(private modalService: MdbModalService) { }

  open(options: PostComposerOptions = {}): Observable<PostComposerResult> {
    const modalRef = this.modalService.open(ModalPostComponent, {
      backdrop: true,
      keyboard: true,
      ignoreBackdropClick: true,
      modalClass: 'modal-md',
      containerClass: '',
      data: this.toModalData(options)
    });

    return modalRef.onClose.pipe(
      take(1),
      map((response: GeoTagMessageData) => this.normalizeResult(response))
    );
  }

  private toModalData(options: PostComposerOptions): object {
    const initialView = options.initialView || 'post';
    let modalState = ModalTracks.PostFormModal;

    if (options.files?.length) {
      modalState = ModalTracks.FilesModal;
    } else if (options.sharedEntity) {
      modalState = ModalTracks.SharedEntityModal;
    } else if (initialView === 'geotag' || initialView === 'boundary') {
      modalState = ModalTracks.GeoTagModal;
    }

    return {
      modalState,
      textInputType: ModalTracks.TextArea,
      triggerPost: options.showAuthor !== false,
      postFiles: options.files || [],
      sharedEntity: options.sharedEntity,
      sharedEntityType: options.sharedEntityType,
      sharedEventStyling: options.sharedEventStyling,
      geotag: options.geotag || {},
      boundary: options.boundary || {},
      geotagModal: options.geotag,
      boundaryModal: options.boundary,
      coordinates: options.coordinates || options.geotag?.coordinates || '',
      isIdentityHidden: options.identityIsHidden,
      isUploadingFile: options.isUploadingFile,
      boundaryView: initialView === 'boundary'
    };
  }

  private normalizeResult(response: GeoTagMessageData): PostComposerResult {
    const created = response?.modalReturnState === ModalTracks.PostComplete
      || response?.modalReturnState === ModalTracks.PostBlobComplete;

    if (!created) {
      return {
        status: 'cancelled',
        geotag: response?.geotag,
        boundary: response?.boundary
      };
    }

    return {
      status: 'created',
      post: response.postData,
      postId: response.postData?.id || response.text,
      geotag: response.geotag,
      boundary: response.boundary
    };
  }
}
